export const STEPS = [
  { id: 'gist', label: '盲听', hint: '关掉字幕完整听一遍，回答大意题' },
  { id: 'intensive', label: '精听', hint: '逐句听，先听再看英文，最后看中文；点单词加入生词本' },
  { id: 'expressions', label: '表达', hint: '学习本集核心表达，听原句，读例句' },
  { id: 'shadowing', label: '跟读', hint: '挑难句录音，与原声对比' },
  { id: 'output', label: '输出', hint: '用本集表达回答问题，说或写' },
] as const;

export type StepId = (typeof STEPS)[number]['id'];

export interface StepRecord {
  done: boolean;
  at?: number;
}

export interface StepState {
  steps: Partial<Record<StepId, StepRecord>>;
  /** Answer index chosen in the gist quiz. */
  gistAnswer?: number;
  /** Segment ids the learner marked as hard during intensive listening. */
  hardSegments?: string[];
  /** Furthest segment index reached in intensive listening. */
  intensiveReached?: number;
  outputText?: string;
}

export const emptyStepState = (): StepState => ({ steps: {} });

export function currentStep(state: StepState): StepId {
  return STEPS.find((s) => !state.steps[s.id]?.done)?.id ?? 'output';
}

export function completedCount(state: StepState): number {
  return STEPS.filter((s) => state.steps[s.id]?.done).length;
}

export function markStep(state: StepState, id: StepId, done = true, now = Date.now()): StepState {
  return { ...state, steps: { ...state.steps, [id]: done ? { done, at: now } : { done } } };
}

/** Merge two copies of the same episode state (local vs server). Completed steps are never lost. */
export function mergeStepState(a: StepState, b: StepState): StepState {
  const steps: StepState['steps'] = {};
  for (const { id } of STEPS) {
    const x = a.steps[id];
    const y = b.steps[id];
    if (x?.done || y?.done) steps[id] = { done: true, at: Math.min(x?.at ?? Infinity, y?.at ?? Infinity) };
  }
  for (const record of Object.values(steps)) if (record.at === Infinity) delete record.at;
  return {
    ...b,
    ...a,
    steps,
    hardSegments: [...new Set([...(a.hardSegments ?? []), ...(b.hardSegments ?? [])])],
    intensiveReached: Math.max(a.intensiveReached ?? 0, b.intensiveReached ?? 0),
  };
}

/**
 * ENGSENCE listen rule: +1 when playback passes 90% of a >5s track, at most
 * once per pass; going back before 10% re-arms the counter.
 */
export class ListenCounter {
  private armed = true;
  constructor(private readonly onComplete: () => void) {}

  update(currentSec: number, durationSec: number): void {
    if (!(durationSec > 5)) return;
    const ratio = currentSec / durationSec;
    if (this.armed && ratio >= 0.9) {
      this.armed = false;
      this.onComplete();
    } else if (!this.armed && ratio < 0.1) {
      this.armed = true;
    }
  }
}
